import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { QueryState } from "@/components/common/AsyncState";
import { addTeacherCertificate, deleteTeacherCertificate, fetchTeacherCertificates } from "@/api/teachers";

interface Props {
  teacherId: string;
  canEdit: boolean;
}

export function TeacherCertificatesTab({ teacherId, canEdit }: Props) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const { data, isLoading, error } = useQuery({
    queryKey: ["teacher-certificates", teacherId],
    queryFn: () => fetchTeacherCertificates(teacherId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["teacher-certificates", teacherId] });
    await queryClient.invalidateQueries({ queryKey: ["teacher", teacherId] });
  };

  const create = useMutation({
    mutationFn: () =>
      addTeacherCertificate(teacherId, {
        name: name.trim(),
        org: org.trim() || null,
        year: year ? Number(year) : null,
      }),
    onSuccess: async () => {
      setAdding(false);
      setName("");
      setOrg("");
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (certificateId: string) => deleteTeacherCertificate(teacherId, certificateId),
    onSuccess: refresh,
  });

  return (
    <Paper className="crm-data-panel" variant="outlined">
      <Stack direction="row" alignItems="center" p={2}>
        <Typography variant="subtitle1" fontWeight={700}>
          Сертификаты и квалификация
        </Typography>
        <span style={{ flexGrow: 1 }} />
        {canEdit && (
          <Button size="small" variant="contained" onClick={() => setAdding(true)}>
            Добавить сертификат
          </Button>
        )}
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Кем выдан</TableCell>
              <TableCell align="right">Год</TableCell>
              {canEdit && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((cert) => (
              <TableRow key={cert.id}>
                <TableCell>
                  <b>{cert.name}</b>
                </TableCell>
                <TableCell>{cert.org ?? "—"}</TableCell>
                <TableCell align="right">{cert.year ?? "—"}</TableCell>
                {canEdit && (
                  <TableCell align="right">
                    <Button size="small" color="inherit" onClick={() => remove.mutate(cert.id)}>
                      Удалить
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {(data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={canEdit ? 4 : 3} align="center">
                  <Typography color="text.secondary" py={2}>
                    Сертификатов пока нет. Преподаватель может добавить их сам через кабинет в Telegram.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>

      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Новый сертификат</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Название"
              size="small"
              fullWidth
              placeholder="CELTA, IELTS Teacher Training…"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Кем выдан"
              size="small"
              fullWidth
              placeholder="Cambridge English"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
            />
            <TextField label="Год" type="number" size="small" fullWidth value={year} onChange={(e) => setYear(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Отмена</Button>
          <Button variant="contained" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
